import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";

// Fixed set, matching the CHECK constraint on activity_reactions in
// migration 075. Not a free picker: see migration 074's comment for why.
export const REACTION_EMOJI = [
  "🔥", "😂", "💀", "😭", "👏", "💰",
  "💩", "👍", "👎", "😡", "🤡", "🐐",
  "🍀", "😤", "🤯", "🙌", "🤝", "🎯",
  "🚀", "🥶", "⚡", "🏆", "💯", "🐔",
];

/**
 * Reactions on a page of the activity feed, keyed by activity id then emoji.
 *
 * One fetch for the whole page rather than one per row: RecentMovesWidget
 * already loads MOVES_LIMIT activities in a single query, and reactions
 * follow the same shape, `activity_id IN (...)`.
 *
 * @param {{leagueId: string, memberId: string|null, activityIds: string[]}} args
 * @returns {{
 *   reactionsByActivity: Record<string, Record<string, {count: number, mine: boolean, memberIds: string[]}>>,
 *   toggle: (activityId: string, emoji: string) => Promise<string|null>,
 * }}
 *   toggle returns an error string to show the user, or null on success.
 */
export function useActivityReactions({ leagueId, memberId, activityIds }) {
  const [reactionsByActivity, setReactionsByActivity] = useState({});

  // Arrays are a new reference every render even with the same ids, so the
  // effect keys off this instead, joined in the order the feed already
  // returned them, which is stable for the page.
  const idsKey = (activityIds || []).join(",");

  useEffect(() => {
    if (!idsKey) {
      setReactionsByActivity({});
      return undefined;
    }
    let cancelled = false;

    supabase
      .from("activity_reactions")
      .select("activity_id, member_id, emoji")
      .in("activity_id", idsKey.split(","))
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          console.error("Error fetching reactions:", error);
          return;
        }
        setReactionsByActivity(groupReactions(data || [], memberId));
      });

    return () => { cancelled = true; };
    // memberId decides `mine`, not which rows are fetched, so it does not
    // need to be in the query, only in the regrouping below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  // memberId can arrive after the first fetch (LeagueContext resolves it
  // separately from the feed's own load), so `mine` is recomputed whenever
  // it changes without a second network round trip.
  useEffect(() => {
    setReactionsByActivity((prev) => regroupMine(prev, memberId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  const toggle = useCallback(async (activityId, emoji) => {
    if (!memberId || !leagueId) return "You need to be a member of this league.";

    const current = reactionsByActivity[activityId]?.[emoji];
    const wasReacted = Boolean(current?.mine);
    const adding = !wasReacted;

    // Optimistic, the same reason useWatchlist's toggle is: a round trip on
    // every tap made a one-row write feel broken on a slow connection.
    setReactionsByActivity((prev) => applyToggle(prev, activityId, emoji, memberId, adding));

    const { error } = wasReacted
      ? await supabase
          .from("activity_reactions")
          .delete()
          .eq("activity_id", activityId)
          .eq("member_id", memberId)
          .eq("emoji", emoji)
      : await supabase
          .from("activity_reactions")
          .insert({ activity_id: activityId, league_id: leagueId, member_id: memberId, emoji });

    if (error) {
      // Revert: put it back to what it was before the optimistic update
      // above, rather than refetching, so a flaky connection does not also
      // cost a round trip to find out it failed.
      setReactionsByActivity((prev) => applyToggle(prev, activityId, emoji, memberId, wasReacted));
      console.error("Reaction write error:", error);
      return "That did not save. Try again.";
    }

    return null;
  }, [memberId, leagueId, reactionsByActivity]);

  return { reactionsByActivity, toggle };
}

function groupReactions(rows, memberId) {
  const byActivity = {};
  rows.forEach((row) => {
    const forActivity = (byActivity[row.activity_id] = byActivity[row.activity_id] || {});
    const forEmoji = (forActivity[row.emoji] = forActivity[row.emoji] || { count: 0, mine: false, memberIds: [] });
    forEmoji.memberIds.push(row.member_id);
    forEmoji.count += 1;
    if (row.member_id === memberId) forEmoji.mine = true;
  });
  return byActivity;
}

function regroupMine(byActivity, memberId) {
  const next = {};
  for (const [activityId, byEmoji] of Object.entries(byActivity)) {
    const nextByEmoji = {};
    for (const [emoji, entry] of Object.entries(byEmoji)) {
      nextByEmoji[emoji] = { ...entry, mine: entry.memberIds.includes(memberId) };
    }
    next[activityId] = nextByEmoji;
  }
  return next;
}

// Adds or removes memberId's reaction for one activity/emoji pair, used both
// for the optimistic write and for its revert on failure.
function applyToggle(byActivity, activityId, emoji, memberId, adding) {
  const forActivity = { ...(byActivity[activityId] || {}) };
  const existing = forActivity[emoji] || { count: 0, mine: false, memberIds: [] };

  if (adding) {
    if (existing.memberIds.includes(memberId)) return byActivity; // already applied
    forActivity[emoji] = {
      count: existing.count + 1,
      mine: true,
      memberIds: [...existing.memberIds, memberId],
    };
  } else {
    const memberIds = existing.memberIds.filter((id) => id !== memberId);
    if (memberIds.length === 0) {
      delete forActivity[emoji];
    } else {
      forActivity[emoji] = { count: memberIds.length, mine: false, memberIds };
    }
  }

  return { ...byActivity, [activityId]: forActivity };
}
